import React from 'react';
import { Link } from 'react-router-dom';
import Card from './Card';

interface ServiceCardProps {
  /**
   * Service ID
   */
  id?: string;
  /**
   * Service title
   */
  title?: string;
  /**
   * Service description
   */
  description?: string;
  /**
   * URL to the service page
   */
  url?: string;
  /**
   * Optional icon name or component
   */
  icon?: React.ReactNode | string;
  /**
   * Optional badge text (e.g., "Popular", "New")
   */
  badge?: string;
  /**
   * Optional additional CSS classes
   */
  className?: string;
  /**
   * Service object (alternative to individual props)
   */
  service?: {
    id: string;
    title: string;
    description: string;
    url: string;
    iconName: string;
    badge?: string;
  };
}

/**
 * Component for displaying service cards with consistent styling
 */
const ServiceCard: React.FC<ServiceCardProps> = ({
  id,
  title,
  description,
  url,
  icon,
  badge,
  className = '',
  service,
}) => {
  // Use service prop if provided, otherwise use individual props
  const serviceId = service?.id || id;
  const serviceTitle = service?.title || title;
  const serviceDescription = service?.description || description;
  const serviceUrl = service?.url || url;
  const serviceBadge = service?.badge || badge;
  const serviceIcon = service?.iconName || icon;
  // Generic placeholder icon if none provided
  const defaultIcon = (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      className="h-6 w-6" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M13 10V3L4 14h7v7l9-11h-7z" 
      />
    </svg>
  );

  return (
    <Link to={serviceUrl || '#'} className="block h-full">
      <Card hover className={`h-full p-6 ${className}`}>
        <div className="flex flex-col h-full">
          {/* Icon and badge */}
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              {typeof serviceIcon === 'string' ? defaultIcon : (serviceIcon || defaultIcon)}
            </div>
            
            {serviceBadge && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {serviceBadge}
              </span>
            )}
          </div>
          
          {/* Content */}
          <h3 className="text-xl font-semibold mb-2">{serviceTitle}</h3>
          <p className="text-neutral-600 mb-4 dark:text-neutral-300">{serviceDescription}</p>
          
          {/* Learn more link */}
          <div className="mt-auto pt-2">
            <span className="inline-flex items-center text-primary font-medium text-sm">
              Learn more
              <svg 
                className="ml-1 w-4 h-4" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default ServiceCard;